import axios from 'axios';
import { attachAuthInterceptors, clearAuthStorage, redirectToAccountsLogin } from '@/services/auth';
import { authActions } from '@/redux/auth/authSlice';

const raw =
  import.meta.env.VITE_CALENDAR_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  'http://localhost:4000';

export const CALENDAR_API_BASE_URL = String(raw).replace(/\/+$/, '');

const axiosInstance = axios.create({
  baseURL: CALENDAR_API_BASE_URL,
  timeout: 20000,
  headers: { Accept: 'application/json' },
});

const SAFE_METHODS = new Set(['get', 'head', 'options']);
const MAX_NETWORK_RETRIES = 4;
const AUTH_401_WINDOW_MS = 15_000;
const AUTH_401_THRESHOLD = 3;

let auth401Timestamps: number[] = [];
let forcedLogoutIssued = false;

const trackAuth401 = (): boolean => {
  const now = Date.now();
  auth401Timestamps = auth401Timestamps.filter((ts) => now - ts <= AUTH_401_WINDOW_MS);
  auth401Timestamps.push(now);
  return auth401Timestamps.length >= AUTH_401_THRESHOLD;
};

async function forceLogout(): Promise<void> {
  if (forcedLogoutIssued) return;
  forcedLogoutIssued = true;
  clearAuthStorage();
  const { default: store } = await import('@/redux/store');
  store.dispatch(authActions.clearAuth());
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (!redirectToAccountsLogin(path || '/calendar')) {
    window.location.replace('/auth');
  }
}

// Attach standard auth interceptors (reads sessionStorage token on request,
// calls refreshToken() only on 401 response).
attachAuthInterceptors(axiosInstance, {
  onAuthFailure: () => {
    if (trackAuth401()) void forceLogout();
  },
});

// Network retry for safe methods only (independent of auth)
axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const req = error.config as (typeof error.config) & { _networkRetry?: number; _authRetry?: boolean };
    const msg = String((error as Error)?.message ?? '');
    const isNetwork =
      (!error.response && !!error.request) || (!error.response && /network error/i.test(msg));
    const isSafe = SAFE_METHODS.has((req?.method ?? '').toLowerCase());
    const attempt = Number(req?._networkRetry ?? 0);

    if (isNetwork && isSafe && attempt < MAX_NETWORK_RETRIES) {
      req._networkRetry = attempt + 1;
      await new Promise((r) => setTimeout(r, 80 * 2 ** attempt));
      return axiosInstance(req);
    }

    // Only count 401s that already went through the auth interceptor retry (_authRetry=true)
    // to avoid double-counting and avoid tracking the initial 401 before the retry attempt.
    if (error.response?.status === 401 && req?._authRetry && trackAuth401()) {
      await forceLogout();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
